import { logger } from '../utils/ProfessionalLogger';
import { safeSetInnerHTML } from '../utils/DOMSanitizer';
/**
 * ErrorHandlerService.ts;
 * Service centralis\u00e9 pour la gestion d'erreurs globale;
 */
export interface ErrorContext {
  component: string;
  action: string;
  userId?: string;
  timestamp: Date;
  userAgent: string;
  url: string;
}
export interface ErrorReport {
  id: string;
  message: string;
  stack?: string;
  context: ErrorContext;
  severity: 'low' | 'medium' | 'high' | 'critical';
  resolved: boolean;
}
export class ErrorHandlerService {
  private static instance: ErrorHandlerService;
  private errors: ErrorReport[] = [];
  private maxErrors: number = 100;
  private isInitialized: boolean = false;
  private constructor() {
    this.setupGlobalErrorHandlers();
  }
  /**
   * Obtient l'instance singleton;
   */
  public static getInstance(): ErrorHandlerService {
    if (!ErrorHandlerService.instance) {
      ErrorHandlerService.instance = new ErrorHandlerService();
    }
    return ErrorHandlerService.instance;
  }
  /**
   * Initialise le service;
   */
  public initialize(): void {
    if (this.isInitialized) return;
    this.setupGlobalErrorHandlers();
    this.isInitialized = true;
    logger.info('\ud83d\udea8 ErrorHandlerService initialis\u00e9');
  }
  /**
   * Configure les gestionnaires d'erreurs globaux;
   */
  private setupGlobalErrorHandlers(): void {
    // Erreurs JavaScript non captur\u00e9es;
    window.addEventListener('error', event => {
      this.handleError(
        event.error,
        {
          component: 'Global',
          action: 'Uncaught Error',
          timestamp: new Date(),
          userAgent: navigator.userAgent,
          url: window.location.href,
        },
        'high'
      );
    });
    // Promesses rejet\u00e9es non g\u00e9r\u00e9es;
    window.addEventListener('unhandledrejection', event => {
      this.handleError(
        new Error(event.reason),
        {
          component: 'Global',
          action: 'Unhandled Promise Rejection',
          timestamp: new Date(),
          userAgent: navigator.userAgent,
          url: window.location.href,
        },
        'high'
      );
    });
    // Erreurs de ressources (images, scripts, etc.)
    window.addEventListener(
      'error',
      event => {
        if (event.target !== window) {
          this.handleError(
            new Error(`Resource error: ${event.target}`),
            {
              component: 'Resource',
              action: 'Load Error',
              timestamp: new Date(),
              userAgent: navigator.userAgent,
              url: window.location.href,
            },
            'medium'
          );
        }
      },
      true
    );
  }
  /**
   * G\u00e8re une erreur;
   */
  public handleError(
    error: Error,
    context: Partial<ErrorContext>,
    severity: ErrorReport['severity'] = 'medium'
  ): void {
    const errorReport: ErrorReport = {
      id: this.generateErrorId(),
      message: error.message,
      stack: error.stack,
      context: {
        component: context.component || 'Unknown',
        action: context.action || 'Unknown',
        timestamp: context.timestamp || new Date(),
        userAgent: context.userAgent || navigator.userAgent,
        url: context.url || window.location.href,
        userId: context.userId,
      },
      severity,
      resolved: false,
    };
    this.errors.push(errorReport);
    // Limiter le nombre d'erreurs stock\u00e9es;
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(-this.maxErrors);
    }
    // Logger l'erreur;
    this.logError(errorReport);
    // Notifier l'utilisateur si l'erreur est critique;
    if (severity === 'critical') {
      this.notifyUser(errorReport);
    }
    // Envoyer \u00e0 un service de monitoring si configur\u00e9;
    this.reportToMonitoring(errorReport);
  }
  /**
   * G\u00e8re une erreur avec try-catch;
   */
  public async handleAsync<T>(
    operation: () => Promise<T>,
    context: Partial<ErrorContext>,
    severity: ErrorReport['severity'] = 'medium'
  ): Promise<T | null> {
    try {
      return await operation();
    } catch (error) {
      this.handleError(error as Error, context, severity);
      return null;
    }
  }
  /**
   * G\u00e8re une erreur synchrone avec try-catch;
   */
  public handleSync<T>(
    operation: () => T,
    context: Partial<ErrorContext>,
    severity: ErrorReport['severity'] = 'medium'
  ): T | null {
    try {
      return operation();
    } catch (error) {
      this.handleError(error as Error, context, severity);
      return null;
    }
  }
  /**
   * G\u00e9n\u00e8re un ID unique pour l'erreur;
   */
  private generateErrorId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  /**
   * Log l'erreur;
   */
  private logError(errorReport: ErrorReport): void {
    const logMessage = `\ud83d\udea8 [${errorReport.severity.toUpperCase()}] ${errorReport.context.component}: ${errorReport.message}`;
    switch (errorReport.severity) {
      case 'critical':
        logger.error(logMessage, errorReport);
        break;
      case 'high':
        logger.error(logMessage, errorReport);
        break;
      case 'medium':
        logger.warn(logMessage, errorReport);
        break;
      case 'low':
        logger.info(logMessage, errorReport);
        break;
    }
  }
  /**
   * Notifie l'utilisateur d'une erreur critique;
   * PHASE 3.4: Émet un événement personnalisé pour ToastProvider
   */
  private notifyUser(errorReport: ErrorReport): void {
    // PHASE 3.4: Émettre un événement personnalisé pour ToastProvider
    const event = new CustomEvent('app-error', {
      detail: {
        type: errorReport.severity === 'critical' ? 'error' : 'warning',
        message: errorReport.message,
        severity: errorReport.severity,
      },
      bubbles: true,
    });
    document.dispatchEvent(event);

    // Fallback: notification DOM si ToastProvider n'est pas disponible
    const notification = document.createElement('div');
    notification.className = 'error-notification';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #ff3b30;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      z-index: 10000;
      max-width: 400px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;
    safeSetInnerHTML(
      notification,
      `
      <div style="font-weight: 600; margin-bottom: 8px;">Erreur critique d\u00e9tect\u00e9e</div>
      <div style="font-size: 14px; opacity: 0.9;">${errorReport.message}</div>
      <button onclick="this.parentElement.remove()" style="
        background: rgba(255,255,255,0.2);
        border: none;
        color: white;
        padding: 8px 16px;
        border-radius: 4px;
        margin-top: 12px;
        cursor: pointer;
      ">Fermer</button>
    `
    );
    document.body.appendChild(notification);
    // Auto-remove after 10 seconds;
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 10000);
  }

  /**
   * PHASE 3.4: Méthode publique pour notifier des erreurs spécifiques avec toasts
   */
  public notifyError(message: string, type: 'error' | 'warning' | 'info' = 'error'): void {
    const event = new CustomEvent('app-error', {
      detail: {
        type,
        message,
        severity: type === 'error' ? 'high' : 'medium',
      },
      bubbles: true,
    });
    document.dispatchEvent(event);
  }
  /**
   * Envoie l'erreur \u00e0 un service de monitoring;
   */
  private reportToMonitoring(errorReport: ErrorReport): void {
    // Ici vous pourriez envoyer \u00e0 Sentry, LogRocket, etc.
    if (errorReport.severity === 'critical' || errorReport.severity === 'high') {
      logger.info("\ud83d\udcca Envoi de l'erreur au service de monitoring:", errorReport.id);
      // Exemple d'envoi \u00e0 un service externe;
      // this.sendToExternalService(errorReport);
    }
  }
  /**
   * Obtient toutes les erreurs;
   */
  public getErrors(): ErrorReport[] {
    return [...this.errors];
  }
  /**
   * Obtient les erreurs par s\u00e9v\u00e9rit\u00e9;
   */
  public getErrorsBySeverity(severity: ErrorReport['severity']): ErrorReport[] {
    return this.errors.filter(error => error.severity === severity);
  }
  /**
   * Obtient les erreurs non r\u00e9solues;
   */
  public getUnresolvedErrors(): ErrorReport[] {
    return this.errors.filter(error => !error.resolved);
  }
  /**
   * Marque une erreur comme r\u00e9solue;
   */
  public resolveError(errorId: string): boolean {
    const error = this.errors.find(e => e.id === errorId);
    if (error) {
      error.resolved = true;
      return true;
    }
    return false;
  }
  /**
   * Nettoie les erreurs r\u00e9solues;
   */
  public clearResolvedErrors(): void {
    this.errors = this.errors.filter(error => !error.resolved);
  }
  /**
   * Nettoie toutes les erreurs;
   */
  public clearAllErrors(): void {
    this.errors = [];
  }
  /**
   * Obtient les statistiques d'erreurs;
   */
  public getErrorStats(): {
    total: number;
    bySeverity: Record<ErrorReport['severity'], number>;
    unresolved: number;
    last24Hours: number;
  } {
    const now = new Date();
    const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    return {
      total: this.errors.length,
      bySeverity: {
        low: this.errors.filter(e => e.severity === 'low').length,
        medium: this.errors.filter(e => e.severity === 'medium').length,
        high: this.errors.filter(e => e.severity === 'high').length,
        critical: this.errors.filter(e => e.severity === 'critical').length,
      },
      unresolved: this.errors.filter(e => !e.resolved).length,
      last24Hours: this.errors.filter(e => e.context.timestamp > last24Hours).length,
    };
  }
  /**
   * Nettoie les ressources;
   */
  public destroy(): void {
    // Les event listeners sont automatiquement nettoy\u00e9s;
    this.errors = [];
    this.isInitialized = false;
  }
}
